import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import type { PostFormData } from '../types/post';

interface PostEditorProps {
  onSubmit: (data: PostFormData) => void;
}

export default function PostEditor({ onSubmit }: PostEditorProps) {
  const { register, handleSubmit, setValue, watch } = useForm<PostFormData>({
    defaultValues: {
      caption: '',
      hashtags: '',
      scheduledDate: '',
      scheduledTime: '',
      timezone: 'UTC',
      image: null
    }
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles[0]) {
      setValue('image', acceptedFiles[0]);
    }
  }, [setValue]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024 // 5MB
  });

  const selectedImage = watch('image');

  const suggestedHashtags = [
    '#photography', '#instagram', '#photooftheday', 
    '#instagood', '#love', '#fashion', '#beautiful', 
    '#art', '#picoftheday', '#nature'
  ];

  const bestTimeSlots = [
    { time: '09:00', engagement: 'High' },
    { time: '12:00', engagement: 'Medium' },
    { time: '15:00', engagement: 'High' },
    { time: '18:00', engagement: 'Very High' },
    { time: '21:00', engagement: 'Medium' }
  ];

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Australia/Sydney'
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Image Upload */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Post Image</label>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400'}`}
        >
          <input {...getInputProps()} />
          {selectedImage ? (
            <div className="space-y-2">
              <img
                src={URL.createObjectURL(selectedImage)}
                alt="Preview"
                className="mx-auto max-h-64 rounded-lg"
              />
              <p className="text-sm text-gray-500">{selectedImage.name}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-gray-600">
                {isDragActive ? 'Drop your image here' : 'Drag & drop an image, or click to select'}
              </p>
              <p className="text-sm text-gray-500">
                Maximum file size: 5MB
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Caption */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Caption</label>
        <textarea
          {...register('caption')}
          rows={4}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
          placeholder="Write your caption here..."
        />
      </div>

      {/* Hashtag Suggestions */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Hashtags</label>
        <input
          type="text"
          {...register('hashtags')}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
          placeholder="Add hashtags (separated by spaces)"
        />
        <div className="flex flex-wrap gap-2 mt-2">
          {suggestedHashtags.map((hashtag) => (
            <button
              key={hashtag}
              type="button"
              onClick={() => {
                const current = watch('hashtags');
                setValue('hashtags', `${current} ${hashtag}`.trim());
              }}
              className="inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200"
            >
              {hashtag}
            </button>
          ))}
        </div>
      </div>

      {/* Scheduling */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Date</label>
          <input
            type="date"
            {...register('scheduledDate')}
            min={new Date().toISOString().split('T')[0]}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Time</label>
          <input
            type="time"
            {...register('scheduledTime')}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
          />
        </div>
      </div>

      {/* Best Time Suggestions */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Recommended Posting Times</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {bestTimeSlots.map((slot) => (
            <button
              key={slot.time}
              type="button"
              onClick={() => setValue('scheduledTime', slot.time)}
              className="text-center p-2 rounded-lg bg-white border border-gray-200 hover:border-primary-300 transition-colors"
            >
              <div className="font-medium text-gray-900">{slot.time}</div>
              <div className="text-sm text-gray-500">{slot.engagement}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Timezone */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Timezone</label>
        <select
          {...register('timezone')}
          className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
        >
          {timezones.map((tz) => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          className="btn btn-primary"
        >
          Schedule Post
        </button>
      </div>
    </form>
  );
}